import React, { useEffect, useMemo, useState } from 'react';
import InfoModal from '../../modals/info-modal/info-modal';
import useInvestorDetailStore from '../../stores/investorDetailStore';
import useTicketSystemEnabled from '../../hooks/useTicketSystemEnabled';
import LogoLight from '../../assets/images/logo/logo-light.svg';
import LogoMulticolor from '../../assets/images/logo/logo-multicolor.svg';
import {
    Navbar,
    TopbarLogo,
    AvatarDropdownItem,
    AvatarDropdownContainer,
    DropdownSubtitle,
    DropdownTitle,
    DropdownHeader,
    DropdownItemLabel,
    Progress,
    ProgressBarContainer,
    AvatarWrapper,
    NotificationDot,
    TopbarMenuItem,
    TopbarMenuWrapper,
    LangToggle,
    LangOption,
} from './topbar.styles';
import MaterialIcon from '../material-icon';
import { colorPalette } from '../../utils/theme';
import { logout } from '../../services/authService';
import useConstructionUpdateStore from '../../stores/constructionUpdateStore';
import useUnitDetailStore from '../../stores/unitDetailStore';
import useDashboardStore from '../../stores/dashboardStore';
import useUnitStore from '../../stores/unitStore';
import { useLocation } from 'react-router-dom';
import { usePWA } from '../../context/PWAContext';
import useIsMobile from '../../hooks/useIsMobile';
import useCustomNavigate from '../../hooks/useCustomNavigate';
import useTopbarStore from '../../stores/topbarStore';
import { DocumentStatus, DocumentType } from '../../services/documentService';
import { useTranslation } from 'react-i18next';
import { tStr } from '../../utils/i18n-helpers';
import { Capacitor } from '@capacitor/core';
import useTenantDetailStore from '../../stores/tenantDetailStore';
import { useContentArea } from '../../context/ContentAreaContext';
import useLanguageStore from '../../stores/languageStore';
import useAlertStore from '../../stores/alertStore';
import { updateInvestorLanguage } from '../../services/investor/investorService';
import { updateTenantLanguage } from '../../services/tenant/tenantService';

interface TopbarProps {
    deactivateSidebar?: boolean;
    theme?: 'light' | 'dark';
    toggleSidebar: () => void;
}

const Topbar: React.FC<TopbarProps> = ({ deactivateSidebar = false, theme = "light", toggleSidebar }) => {
    const navigate = useCustomNavigate();
    const location = useLocation();
    const isPWA = usePWA();
    const isCapacitor = Capacitor.isNativePlatform();
    const isMobile = useIsMobile();
    const { t, i18n } = useTranslation();
    const ticketsEnabled = useTicketSystemEnabled();
    const { contentAreaRef } = useContentArea();

    // Stores
    const { investor, reset: resetInvestoreDetailStore } = useInvestorDetailStore();
    const { tenant, reset: resetTenantDetailStore } = useTenantDetailStore();
    // The tenant portal is English-only (no German translations), so tenants get
    // no language switch. Investors (and investor+tenant "both" users, who have
    // investor data) keep the DE/EN switch.
    const canSwitchLanguage = !!investor;
    const { isProgressBarVisible, progress } = useTopbarStore();
    const showAlert = useAlertStore(state => state.showAlert);
    const { setLanguage } = useLanguageStore();
    const resetUnitStore = useUnitStore(state => state.reset);
    const resetUnitDetailStore = useUnitDetailStore(state => state.reset);
    const resetConstructionUpdateStore = useConstructionUpdateStore(state => state.reset);
    const resetDashboardStore = useDashboardStore(state => state.reset);

    // States
    const [isHistoryAvailable, setIsHistoryAvailable] = useState(false);
    const [isInfoModalVisible, setIsInfoModalVisible] = useState<boolean>(false);
    const [showDropdown, setShowDropdown] = useState<boolean>(false);
    const [isScrolled, setIsScrolled] = useState<boolean>(false);

    // Set history available
    useEffect(() => {
        const depth = (navigate as any).getHistoryDepth?.();
        setIsHistoryAvailable(depth > 0);
    }, [navigate]);

    // Handle scroll for blur effect
    useEffect(() => {
        const handleScroll = () => {
            if (contentAreaRef.current) {
                const scrollTop = contentAreaRef.current.scrollTop;
                setIsScrolled(scrollTop > 0);
            }
        };

        const contentElement = contentAreaRef.current;
        if (contentElement) {
            contentElement.addEventListener('scroll', handleScroll);
            return () => contentElement.removeEventListener('scroll', handleScroll);
        }
    }, [contentAreaRef]);

    // Functions
    const onClickLogout = async () => {
        await logout();

        resetUnitStore();
        resetConstructionUpdateStore();
        resetInvestoreDetailStore();
        resetTenantDetailStore();
        resetUnitDetailStore();
        resetDashboardStore();

        navigate("/login", { replace: true, clearHistory: true });
    };

    // Switches the UI language and persists it to the backend (same flow as the
    // settings page). Kept open so the user sees the active checkmark move.
    const changeLanguage = async (lng: string) => {
        if (i18n.language === lng) return;
        i18n.changeLanguage(lng);
        setLanguage(lng);

        try {
            if (investor) {
                await updateInvestorLanguage(lng);
            } else if (tenant) {
                await updateTenantLanguage(lng);
            }
        } catch (error) {
            console.error("Error saving language preference:", error);
        }

        showAlert("success", lng === 'de'
            ? tStr(t, "pages.settings.alerts.language_changed_de")
            : tStr(t, "pages.settings.alerts.language_changed_en"));
    };

    // Memos
    const themeColor = useMemo(() => {
        if (theme === "dark") return colorPalette.white;
        return colorPalette.primaryDark;
    }, [theme]);

    // Investor Account Memos
    const hasAddress = useMemo(() => {
        if (!investor) return false;
        if (tenant) return true;
        return investor.address.country !== null || investor.address.country !== undefined;
    }, [investor, tenant]);

    const expiredDocuments = useMemo(() => {
        if (investor) {
            return investor.documents.filter(doc => {
                return doc.valid_until !== null && new Date(doc.valid_until) < new Date();
            });
        } else if (tenant) {
            return tenant.documents.filter(doc => {
                return doc.valid_until !== null && new Date(doc.valid_until) < new Date();
            });
        } else {
            return [];
        }
    }, [investor, tenant]);

    const declinedDocuments = useMemo(() => {
        if (investor) {
            return investor.documents.filter(doc => {
                return doc.status === DocumentStatus.DECLINED;
            });
        } else if (tenant) {
            return tenant.documents.filter(doc => {
                return doc.status === DocumentStatus.DECLINED;
            });
        } else {
            return [];
        }
    }, [investor, tenant]);

    const inReviewDocuments = useMemo(() => {
        if (investor) {
            return investor.documents.filter(doc => {
                return doc.status === DocumentStatus.IN_REVIEW;
            });
        } else if (tenant) {
            return tenant.documents.filter(doc => {
                return doc.status === DocumentStatus.IN_REVIEW;
            });
        } else {
            return [];
        }
    }, [investor, tenant]);

    const hasMandatoryDocumentsUploaded = useMemo(() => {
        if (investor) {
            return investor.documents.filter(document => document.document_type === DocumentType.PASSPORT || document.document_type === DocumentType.PROOF_OF_ADDRESS).length > 1
        } else if (tenant) {
            return tenant.documents.filter(document => document.document_type === DocumentType.PASSPORT || document.document_type === DocumentType.EMIRATES_ID).length > 1
        } else {
            return false;
        }
    }, [investor, tenant]);

    // Header States
    const headerStatus = useMemo(() => {
        if (!hasAddress && !hasMandatoryDocumentsUploaded) return "inactive";
        if (!hasMandatoryDocumentsUploaded || expiredDocuments.length > 0 || declinedDocuments.length > 0) return "inactive";
        if (inReviewDocuments.length > 0) return "in_review";
        return "active";
    }, [expiredDocuments, hasAddress, hasMandatoryDocumentsUploaded, declinedDocuments, inReviewDocuments]);

    // A conversation opened from a push or mail link has no history, and the tab
    // bar is hidden there, so the arrow falls back to the request list.
    const inConversation = /^\/requests\/\d+/.test(location.pathname);

    const showMenu = useMemo(() => {
        if (location.pathname.includes("/onboarding")) return false;
        return true;
    }, [location]);

    return (
        <>
            <Navbar className="navbar navbar-expand-lg navbar-dark sticky-top" theme={theme} isScrolled={isScrolled}>
                <div className="container-fluid d-flex justify-content-between me-md-4 ms-md-4">
                    <div className="d-flex align-items-center" style={{ gap: 8, cursor: "pointer" }}>
                        {isMobile && (isHistoryAvailable || inConversation) && (
                            <MaterialIcon
                                icon="arrow_back_ios"
                                color={themeColor}
                                size={20}
                                onClick={() => (isHistoryAvailable ? navigate(-1) : navigate("/requests"))}
                            />
                        )}
                        <TopbarLogo src={theme === "light" ? LogoMulticolor : LogoLight} alt="Logo" onClick={() => navigate("/", { replace: true, clearHistory: true })} />
                    </div>

                    {showMenu && (
                        <div className="d-none d-lg-flex justify-content-center flex-grow-1">
                            <TopbarMenuWrapper theme={theme}>
                                {investor ? (
                                    <>
                                        <TopbarMenuItem
                                            theme={theme}
                                            active={location.pathname === "/dashboard"}
                                            onClick={() => navigate("/", { replace: true, clearHistory: true })}
                                        >
                                            {t('tab_bar.landlord.dashboard')}
                                        </TopbarMenuItem>
                                        <TopbarMenuItem
                                            theme={theme}
                                            active={location.pathname.startsWith("/my-units")}
                                            onClick={() => navigate("/my-units")}
                                        >
                                            {t('tab_bar.landlord.units')}
                                        </TopbarMenuItem>
                                        <TopbarMenuItem
                                            theme={theme}
                                            active={location.pathname.startsWith("/financials")}
                                            onClick={() => navigate("/financials")}
                                        >
                                            {t('tab_bar.landlord.finances')}
                                        </TopbarMenuItem>
                                        <TopbarMenuItem
                                            theme={theme}
                                            active={location.pathname.startsWith("/contracts")}
                                            onClick={() => navigate("/contracts")}
                                        >
                                            {t('tab_bar.landlord.contracts')}
                                        </TopbarMenuItem>
                                        {ticketsEnabled && (
                                            <TopbarMenuItem
                                                theme={theme}
                                                active={location.pathname.startsWith("/requests")}
                                                onClick={() => navigate("/requests")}
                                                data-tour="support-nav"
                                            >
                                                {t('tab_bar.landlord.requests')}
                                            </TopbarMenuItem>
                                        )}
                                        <TopbarMenuItem
                                            theme={theme}
                                            active={location.pathname.startsWith("/knowledge-hub")}
                                            onClick={() => navigate("/knowledge-hub")}
                                        >
                                            {t('tab_bar.landlord.knowledge_hub')}
                                        </TopbarMenuItem>
                                    </>
                                ) : (
                                    <>
                                        <TopbarMenuItem
                                            theme={theme}
                                            active={location.pathname === "/dashboard"}
                                            onClick={() => navigate("/", { replace: true, clearHistory: true })}
                                        >
                                            {t('tab_bar.tenant.dashboard')}
                                        </TopbarMenuItem>
                                        <TopbarMenuItem
                                            theme={theme}
                                            active={location.pathname.startsWith("/my-unit")}
                                            onClick={() => navigate("/my-unit")}
                                        >
                                            {t('tab_bar.tenant.unit')}
                                        </TopbarMenuItem>
                                        <TopbarMenuItem
                                            theme={theme}
                                            active={location.pathname.startsWith("/payments")}
                                            onClick={() => navigate("/payments")}
                                        >
                                            {t('tab_bar.tenant.payments')}
                                        </TopbarMenuItem>
                                        {ticketsEnabled && (
                                            <TopbarMenuItem
                                                theme={theme}
                                                active={location.pathname.startsWith("/requests")}
                                                onClick={() => navigate("/requests")}
                                            >
                                                {t('tab_bar.landlord.requests')}
                                            </TopbarMenuItem>
                                        )}
                                    </>
                                )}
                            </TopbarMenuWrapper>
                        </div>
                    )}

                    <div className="d-flex align-items-center" style={{ gap: 12 }}>
                        {/* Mobile language switch — the avatar dropdown (desktop) is
                            hidden on mobile, so surface DE/EN directly in the topbar. */}
                        {isMobile && canSwitchLanguage && (
                            <LangToggle>
                                <LangOption $active={i18n.language === 'de'} onClick={() => changeLanguage('de')}>DE</LangOption>
                                <LangOption $active={i18n.language !== 'de'} onClick={() => changeLanguage('en')}>EN</LangOption>
                            </LangToggle>
                        )}
                        {!deactivateSidebar && !isPWA && !isCapacitor && (
                            <span className="d-flex d-lg-none" data-tour="support-menu">
                                <MaterialIcon icon="menu" color={themeColor} size={24} onClick={toggleSidebar} />
                            </span>
                        )}

                        {/* Avatar and Dropdown */}
                        <div
                            onMouseEnter={isCapacitor ? undefined : () => setShowDropdown(true)}
                            onMouseLeave={isCapacitor ? undefined : () => setShowDropdown(false)}
                            onClick={isCapacitor ? () => navigate("/profile", { replace: true, clearHistory: true }) : undefined}
                            className="position-relative"
                        >
                            <div className="d-flex align-items-center" style={{ gap: isMobile ? 12 : 18 }}>
                                {/* Show notification dot if `hasNotification` is true */}
                                {/*<MaterialIcon icon="notifications_active" size={24} color={themeColor} />*/}
                                {(!isMobile || (isCapacitor && !location.pathname.includes("profile"))) && (
                                    <AvatarWrapper>
                                        <MaterialIcon icon="account_circle" size={24} color={themeColor} />
                                        {headerStatus === "inactive" && <NotificationDot />}
                                    </AvatarWrapper>
                                )}
                            </div>

                            {showDropdown && (
                                <AvatarDropdownContainer>
                                    <DropdownHeader>
                                        <DropdownTitle className="mb-0">{investor ? investor.first_name : tenant?.first_name} {investor ? investor.last_name : tenant?.last_name}</DropdownTitle>
                                        <DropdownSubtitle>{investor ? investor.email : tenant?.email}</DropdownSubtitle>
                                    </DropdownHeader>
                                    <AvatarDropdownItem onClick={() => {
                                        setShowDropdown(false);
                                        navigate("/profile", { replace: true, clearHistory: true });
                                    }}>
                                        <MaterialIcon icon="account_circle" size={20} color={colorPalette.textMain} />
                                        <DropdownItemLabel>{tStr(t, "components.topbar.my_account")}</DropdownItemLabel>
                                    </AvatarDropdownItem>
                                    <AvatarDropdownItem onClick={() => {
                                        setShowDropdown(false);
                                        navigate("/profile/settings");
                                    }}>
                                        <MaterialIcon icon="manage_accounts" size={20} color={colorPalette.textMain} />
                                        <DropdownItemLabel>{tStr(t, "components.topbar.settings")}</DropdownItemLabel>
                                    </AvatarDropdownItem>
                                    {canSwitchLanguage && (
                                        <>
                                            <AvatarDropdownItem onClick={() => changeLanguage('de')}>
                                                <span style={{ fontSize: 18, lineHeight: 1 }}>🇩🇪</span>
                                                <DropdownItemLabel>{tStr(t, "pages.settings.language.german")}</DropdownItemLabel>
                                                {i18n.language === 'de' && (
                                                    <span style={{ marginLeft: 'auto', display: 'inline-flex' }}>
                                                        <MaterialIcon icon="check" size={18} color={colorPalette.blue500} />
                                                    </span>
                                                )}
                                            </AvatarDropdownItem>
                                            <AvatarDropdownItem onClick={() => changeLanguage('en')}>
                                                <span style={{ fontSize: 18, lineHeight: 1 }}>🇬🇧</span>
                                                <DropdownItemLabel>{tStr(t, "pages.settings.language.english")}</DropdownItemLabel>
                                                {i18n.language === 'en' && (
                                                    <span style={{ marginLeft: 'auto', display: 'inline-flex' }}>
                                                        <MaterialIcon icon="check" size={18} color={colorPalette.blue500} />
                                                    </span>
                                                )}
                                            </AvatarDropdownItem>
                                        </>
                                    )}
                                    <AvatarDropdownItem onClick={onClickLogout}>
                                        <MaterialIcon icon="logout" size={20} color={colorPalette.textMain} />
                                        <DropdownItemLabel>{tStr(t, "components.topbar.logout")}</DropdownItemLabel>
                                    </AvatarDropdownItem>
                                </AvatarDropdownContainer>
                            )}
                        </div>
                    </div>
                </div>
                {isInfoModalVisible && (
                    <InfoModal onClose={() => setIsInfoModalVisible(false)} />
                )}
            </Navbar>

            {isProgressBarVisible && (
                <ProgressBarContainer>
                    <Progress width={progress} />
                </ProgressBarContainer>
            )}
        </>
    );
};

export default Topbar;
